import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { CloudDownload, ExternalLink, Loader2, Pencil, Play, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { Alert, Dialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Pagination } from '@components/Pagination';
import { ActionButton } from '@components/Table';
import mediaService from '@services/mediaService';
import type { Pagination as PaginationInfo } from '@/types/activity';
import type { MediaImportItem, MediaImportItemStatus, MediaImportSource, MediaImportSourceFormData } from '@/types/media';
import { IMPORT_ITEM_STATUS_LABELS } from '@/types/media';
import { formatBytes, formatDateTimeBR } from '@/utils/formatters';

const PAGE_SIZE = 50;

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  drive_owner_upn: 'Dono do OneDrive (UPN)',
  folder_path: 'Pasta',
  include_subfolders: 'Incluir subpastas',
  is_active: 'Ativa',
};

const ITEM_BADGE: Record<MediaImportItemStatus, string> = {
  pending: 'badge-neutral',
  imported: 'badge-success',
  skipped: 'badge-warning',
  error: 'badge-danger',
};

/** Badge do último status de execução (texto livre do worker). */
const RunStatus: React.FC<{ status?: string }> = ({ status }) => {
  if (!status) return <span className="text-slate-300">—</span>;
  const s = status.toLowerCase();
  const cls = /erro|error|fail|falh/.test(s)
    ? 'badge-danger'
    : /run|exec|process|andamento/.test(s)
      ? 'badge-warning'
      : /ok|success|sucesso|done|conclu/.test(s)
        ? 'badge-success'
        : 'badge-neutral';
  return <span className={cls}>{status}</span>;
};

/** Importação do OneDrive: fontes (pastas) e os itens que cada execução encontrou. */
export const MediaImportView: React.FC = () => {
  const [sources, setSources] = useState<MediaImportSource[]>([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [editing, setEditing] = useState<MediaImportSource | 'new' | null>(null);
  const [runningId, setRunningId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<MediaImportItemStatus | ''>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<MediaImportItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loadingItems, setLoadingItems] = useState(true);

  const loadSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      setSources(await mediaService.importSources());
    } catch (err) {
      setError(err);
    } finally {
      setLoadingSources(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await mediaService.importItems({
        sourceId: sourceFilter ? Number(sourceFilter) : undefined,
        status: statusFilter,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingItems(false);
    }
  }, [sourceFilter, statusFilter, page]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const run = async (source: MediaImportSource) => {
    setRunningId(source.id);
    try {
      await mediaService.runImportSource(source.id);
      setNotice(`Importação de "${source.name}" enviada para execução. Atualize em alguns instantes para ver o andamento.`);
      loadSources();
    } catch (err) {
      setError(err);
    } finally {
      setRunningId(null);
    }
  };

  const sourceName = (id: number) => sources.find((s) => s.id === id)?.name ?? `#${id}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <CloudDownload size={20} className="text-slate-500" /> Importação do OneDrive
        </h1>
        <p className="basis-full order-last text-small -mt-2">
          Importação em massa do OneDrive — somente administradores.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<RefreshCw />}
            onClick={() => {
              loadSources();
              loadItems();
            }}
          >
            Atualizar
          </Button>
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setEditing('new')}>
            Nova fonte
          </Button>
        </div>
      </div>

      {notice && (
        <Alert variant="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* Fontes */}
      <div className="table-container bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Fonte</th>
                <th>Pasta</th>
                <th>Última execução</th>
                <th className="text-right">Itens</th>
                <th>Situação</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {loadingSources && sources.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loadingSources && sources.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-10 text-slate-500">
                    Nenhuma fonte cadastrada. Cadastre uma pasta do OneDrive para importar as fotos e vídeos dela.
                  </td>
                </tr>
              )}
              {sources.map((s) => (
                <tr key={s.id} className={clsx('table-row', !s.is_active && 'opacity-60')}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">{s.name}</p>
                    <p className="text-small">{s.drive_owner_upn}</p>
                  </td>
                  <td className="table-cell">
                    <p className="font-mono text-xs text-slate-700">{s.folder_path || '/'}</p>
                    <p className="text-small">{s.include_subfolders ? 'Com subpastas' : 'Só a pasta'}</p>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <RunStatus status={s.last_status} />
                      <span className="text-small whitespace-nowrap">{formatDateTimeBR(s.last_run_at)}</span>
                    </div>
                    {s.last_message && (
                      <p className="text-small mt-1 max-w-xs line-clamp-2" title={s.last_message}>
                        {s.last_message}
                      </p>
                    )}
                  </td>
                  <td className="table-cell text-right text-xs tabular-nums whitespace-nowrap">
                    <p className="text-slate-800 text-sm">{s.items_total.toLocaleString('pt-BR')} no total</p>
                    <p className="text-emerald-700">{s.items_imported.toLocaleString('pt-BR')} importados</p>
                    <p className="text-amber-700">{s.items_skipped.toLocaleString('pt-BR')} ignorados</p>
                    {s.items_error > 0 && <p className="text-red-600">{s.items_error.toLocaleString('pt-BR')} com erro</p>}
                  </td>
                  <td className="table-cell">
                    {s.is_active ? <span className="badge-success">Ativa</span> : <span className="badge-neutral">Inativa</span>}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Play />}
                        loading={runningId === s.id}
                        disabled={!s.is_active}
                        onClick={() => run(s)}
                        title={s.is_active ? 'Executar agora' : 'Fonte inativa'}
                      >
                        Executar agora
                      </Button>
                      <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditing(s)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Itens */}
      <div className="flex flex-wrap items-end gap-3 pt-2">
        <h2 className="text-h4 mr-auto">Itens encontrados</h2>
        <div>
          <label className="text-label block mb-1">Fonte</label>
          <select
            className="select-field w-56"
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todas</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-label block mb-1">Situação</label>
          <select
            className="select-field w-44"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as MediaImportItemStatus | '');
              setPage(1);
            }}
          >
            <option value="">Todas</option>
            {(Object.keys(IMPORT_ITEM_STATUS_LABELS) as MediaImportItemStatus[]).map((s) => (
              <option key={s} value={s}>
                {IMPORT_ITEM_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-container bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Arquivo</th>
                <th>Fonte</th>
                <th className="text-right">Tamanho</th>
                <th>Situação</th>
                <th>Atualizado em</th>
                <th>Mídia</th>
              </tr>
            </thead>
            <tbody>
              {loadingItems && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loadingItems && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-10 text-slate-500">
                    Nenhum item.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className={clsx('table-row', loadingItems && 'opacity-60')}>
                  <td className="table-cell max-w-md">
                    <p className="font-mono text-xs text-slate-700 break-all">{it.path}</p>
                    {it.error && <p className="text-xs text-red-600 mt-1">{it.error}</p>}
                  </td>
                  <td className="table-cell whitespace-nowrap">{sourceName(it.source_id)}</td>
                  <td className="table-cell text-right whitespace-nowrap tabular-nums">{formatBytes(it.size_bytes)}</td>
                  <td className="table-cell">
                    <span className={ITEM_BADGE[it.status]}>{IMPORT_ITEM_STATUS_LABELS[it.status]}</span>
                  </td>
                  <td className="table-cell whitespace-nowrap">{formatDateTimeBR(it.updated_at)}</td>
                  <td className="table-cell">
                    {it.media_id ? (
                      <Link to={`/media?media=${it.media_id}`} className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                        #{it.media_id} <ExternalLink size={12} />
                      </Link>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total_items}
            onChange={setPage}
          />
        )}
      </div>

      {editing && (
        <SourceDialog
          source={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadSources();
          }}
          onError={setError}
        />
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} fieldLabels={FIELD_LABELS} />
    </div>
  );
};

const SourceDialog: React.FC<{
  source: MediaImportSource | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}> = ({ source, onClose, onSaved, onError }) => {
  const [form, setForm] = useState<MediaImportSourceFormData>({
    name: source?.name ?? '',
    drive_owner_upn: source?.drive_owner_upn ?? '',
    folder_path: source?.folder_path ?? '',
    include_subfolders: source?.include_subfolders ?? true,
    is_active: source?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof MediaImportSourceFormData>(key: K, value: MediaImportSourceFormData[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    const data: MediaImportSourceFormData = {
      ...form,
      name: form.name.trim(),
      drive_owner_upn: form.drive_owner_upn.trim(),
      folder_path: form.folder_path.trim().replace(/^\/+|\/+$/g, ''),
    };
    try {
      if (source) await mediaService.updateImportSource(source.id, data);
      else await mediaService.createImportSource(data);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={source ? 'Editar fonte de importação' : 'Nova fonte de importação'}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={!form.name.trim() || !form.drive_owner_upn.trim()}>
            Salvar
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="text-label block mb-1">Nome</label>
          <input className="input-field" value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus placeholder="Ex.: Fotos de produtos" />
        </div>
        <div>
          <label className="text-label block mb-1">Dono do OneDrive (UPN)</label>
          <input
            className="input-field"
            value={form.drive_owner_upn}
            onChange={(e) => set('drive_owner_upn', e.target.value)}
            placeholder="marketing@viverorganizer.com.br"
          />
        </div>
        <div>
          <label className="text-label block mb-1">Pasta (relativa à raiz do drive)</label>
          <input
            className="input-field font-mono"
            value={form.folder_path}
            onChange={(e) => set('folder_path', e.target.value)}
            placeholder="Marketing/Fotos Produtos"
          />
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={form.include_subfolders}
              onChange={(e) => set('include_subfolders', e.target.checked)}
            />
            Incluir subpastas
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
            Ativa
          </label>
        </div>
        <p className="text-small">Cada execução é incremental: só traz o que mudou desde a anterior, sem reimportar.</p>
      </div>
    </Dialog>
  );
};
